/**
 * Industry-Specific Configuration
 *
 * Centralized per-industry KPI mappings, optimization weights, confidence
 * baselines, and demo proof points. This is the single source of truth that
 * connects funnel templates → budget optimization → confidence scoring.
 *
 * Industries are keyed by the same `industry` string used in FunnelConfig.
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * KPI definition for a specific industry.
 * Maps abstract KPI names to display labels and calculation types.
 */
export interface IndustryKPI {
  /** Machine key (e.g., 'revenue', 'leads', 'roas', 'cpl') */
  key: string
  /** Thai display label */
  label: string
  /** English label */
  labelEn: string
  /** Whether higher is better (true) or lower is better (false) */
  higherIsBetter: boolean
  /** Unit for display (e.g., '฿', '%', 'x') */
  unit: string
}

/**
 * Optimization weights per funnel stage for an industry.
 * These multiply the Hill saturation contribution during budget optimization
 * to shift allocation toward stages that matter more for the industry.
 *
 * Values are relative (sum does not need to be 1.0). A higher weight means
 * the optimizer prefers to allocate budget to that stage.
 */
export interface IndustryOptimizationWeights {
  /** Weight for awareness-stage budget (0-2 scale, 1.0 = neutral) */
  awareness: number
  /** Weight for consideration-stage budget */
  consideration: number
  /** Weight for conversion-stage budget */
  conversion: number
  /** Weight for retention-stage budget (0 if industry has no retention tracking) */
  retention: number
}

/**
 * Industry-specific confidence baseline and tuning.
 * The base confidence before dynamic penalties is adjusted per industry
 * based on typical data quality and model reliability.
 */
export interface IndustryConfidenceConfig {
  /** Base confidence percentage (0-100) before dynamic penalties */
  baseConfidence: number
  /**
   * Sensitivity multiplier for budget deviation penalties.
   * Higher = confidence drops faster when budgets deviate from optimal.
   * 1.0 = standard sensitivity, 0.8 = more forgiving, 1.2 = stricter.
   */
  deviationSensitivity: number
  /** Floor for confidence (never drops below this) */
  floor: number
}

/**
 * Demo proof point for the industry — verifiable, anonymized.
 * Used in reports and the demo narrative.
 */
export interface IndustryProofPoint {
  /** What happened (anonymized, by industry not by client) */
  claim: string
  /** Thai summary */
  claimTh: string
  /** Supporting metric */
  metric: string
}

/**
 * Complete industry configuration.
 */
export interface IndustryConfig {
  /** Industry key — must match FunnelConfig.industry */
  industry: string
  /** Thai display name */
  labelTh: string
  /** English display name */
  labelEn: string
  /** Primary KPI type for this industry */
  kpiType: 'revenue' | 'leads'
  /** All tracked KPIs for this industry */
  kpis: IndustryKPI[]
  /** Funnel stages optimization weights */
  optimizationWeights: IndustryOptimizationWeights
  /** Confidence scoring config */
  confidence: IndustryConfidenceConfig
  /** Retention stage support (4th stage beyond the standard 3) */
  hasRetentionStage: boolean
  /** Retention metric label (if hasRetentionStage is true) */
  retentionMetric?: string
  /** Demo proof points */
  proofPoints: IndustryProofPoint[]
  /** Typical CAC range in THB */
  cacRange: [number, number]
  /** Typical conversion value in THB */
  avgConversionValue: number
}

// ============================================================================
// INDUSTRY DEFINITIONS
// ============================================================================

/**
 * Supplement Brand (e.g., vitamins, wellness products)
 *
 * Demo proof point: supplement brand 8M→4M budget cut, sales flat.
 * Key insight: awareness is over-invested; budget shifts to conversion.
 */
export const SUPPLEMENT_BRAND_CONFIG: IndustryConfig = {
  industry: 'supplement',
  labelTh: 'แบรนด์อาหารเสริม',
  labelEn: 'Supplement Brand',
  kpiType: 'revenue',
  kpis: [
    { key: 'revenue', label: 'ยอดขาย', labelEn: 'Revenue', higherIsBetter: true, unit: '฿' },
    { key: 'roas', label: 'ROAS', labelEn: 'ROAS', higherIsBetter: true, unit: 'x' },
    { key: 'cpa', label: 'ต้นทุนต่อการซื้อ', labelEn: 'CPA', higherIsBetter: false, unit: '฿' },
    { key: 'repeat_rate', label: 'อัตราซื้อซ้ำ', labelEn: 'Repeat Rate', higherIsBetter: true, unit: '%' },
    { key: 'aov', label: 'ยอดเฉลี่ยต่อคำสั่ง', labelEn: 'AOV', higherIsBetter: true, unit: '฿' },
  ],
  optimizationWeights: {
    // Supplement brands over-invest in awareness; optimizer shifts to conversion
    awareness: 0.6,
    consideration: 1.0,
    conversion: 1.4,
    retention: 1.2,
  },
  confidence: {
    baseConfidence: 84,
    deviationSensitivity: 1.1,
    floor: 55,
  },
  hasRetentionStage: true,
  retentionMetric: 'repeat_purchases',
  proofPoints: [
    {
      claim: 'Supplement brand cut budget from 8M to 4M THB/month while sales remained flat',
      claimTh: 'แบรนด์อาหารเสริมลดงบจาก 8M เหลือ 4M บาท/เดือน ยอดขายคงที่',
      metric: 'Revenue maintained at -50% spend',
    },
    {
      claim: 'Reallocated 40% of awareness budget to conversion-stage campaigns',
      claimTh: 'ปรับงบจาก Brand Awareness 40% ไปยัง Conversion',
      metric: '+22% conversion rate',
    },
  ],
  cacRange: [180, 450],
  avgConversionValue: 2400,
}

/**
 * EV (Electric Vehicle) brand
 *
 * Demo proof point: EV brand -60% CPL after optimization.
 * Long consideration cycle, test drive is the key micro-conversion.
 */
export const EV_BRAND_CONFIG: IndustryConfig = {
  industry: 'ev',
  labelTh: 'รถยนต์ไฟฟ้า',
  labelEn: 'EV Brand',
  kpiType: 'leads',
  kpis: [
    { key: 'leads', label: 'จำนวน Lead', labelEn: 'Leads', higherIsBetter: true, unit: '' },
    { key: 'cpl', label: 'ต้นทุนต่อ Lead', labelEn: 'CPL', higherIsBetter: false, unit: '฿' },
    { key: 'test_drive_rate', label: 'อัตรานัดทดลองขับ', labelEn: 'Test Drive Rate', higherIsBetter: true, unit: '%' },
    { key: 'reservation', label: 'การจองรถ', labelEn: 'Reservations', higherIsBetter: true, unit: '' },
    { key: 'roas', label: 'ROAS', labelEn: 'ROAS', higherIsBetter: true, unit: 'x' },
  ],
  optimizationWeights: {
    // EV has a long consideration phase — test drives are the critical micro-conversion
    awareness: 0.9,
    consideration: 1.3,
    conversion: 1.1,
    retention: 0.5,
  },
  confidence: {
    baseConfidence: 87,
    deviationSensitivity: 0.9, // More forgiving — long sales cycle means more noise
    floor: 58,
  },
  hasRetentionStage: false, // EV purchase is one-time (service is separate)
  proofPoints: [
    {
      claim: 'EV brand achieved -60% CPL after shifting budget from display to search retargeting',
      claimTh: 'แบรนด์รถยนต์ไฟฟ้าลด CPL ได้ 60% หลังปรับงบจาก Display ไป Search Retargeting',
      metric: 'CPL reduced from ฿12,000 to ฿4,800',
    },
    {
      claim: 'Test drive conversion improved 35% with cross-channel synergy optimization',
      claimTh: 'อัตรานัดทดลองขับดีขึ้น 35% จากการเพิ่มมูลค่าจากหลายช่องทาง (Cross-channel synergy)',
      metric: 'Test drive rate: 8.2% → 11.1%',
    },
  ],
  cacRange: [3000, 12000],
  avgConversionValue: 85000,
}

/**
 * FMCG (Fast-Moving Consumer Goods)
 *
 * High-volume, low-margin, brand-driven. Awareness still matters
 * but the optimizer pushes toward conversion-promo mix.
 */
export const FMCG_CONFIG: IndustryConfig = {
  industry: 'fmcg',
  labelTh: 'FMCG / สินค้าอุปโภคบริโภค',
  labelEn: 'FMCG',
  kpiType: 'revenue',
  kpis: [
    { key: 'revenue', label: 'ยอดขาย', labelEn: 'Revenue', higherIsBetter: true, unit: '฿' },
    { key: 'market_share', label: 'ส่วนแบ่งตลาด', labelEn: 'Market Share', higherIsBetter: true, unit: '%' },
    { key: 'shelf_velocity', label: 'อัตราการหมุนเวียนสินค้า', labelEn: 'Shelf Velocity', higherIsBetter: true, unit: '' },
    { key: 'brand_recall', label: 'การรับรู้แบรนด์', labelEn: 'Brand Recall', higherIsBetter: true, unit: '%' },
    { key: 'roas', label: 'ROAS', labelEn: 'ROAS', higherIsBetter: true, unit: 'x' },
  ],
  optimizationWeights: {
    // FMCG needs strong awareness + conversion balance (promo-driven)
    awareness: 1.2,
    consideration: 0.9,
    conversion: 1.2,
    retention: 0.8,
  },
  confidence: {
    baseConfidence: 79,
    deviationSensitivity: 1.0,
    floor: 52,
  },
  hasRetentionStage: true,
  retentionMetric: 'repeat_purchases',
  proofPoints: [
    {
      claim: 'FMCG brand increased market share by 3.2pt with budget rebalancing across 4 channels',
      claimTh: 'แบรนด์ FMCG เพิ่มส่วนแบ่งตลาด 3.2pt จากการปรับสมดุลงบ 4 ช่องทาง',
      metric: 'Market share: 18.4% → 21.6%',
    },
  ],
  cacRange: [80, 250],
  avgConversionValue: 320,
}

// ============================================================================
// EXISTING INDUSTRIES (retroactively defined for consistency)
// ============================================================================

/**
 * Lead Generation (current demo default)
 */
export const LEAD_GEN_INDUSTRY_CONFIG: IndustryConfig = {
  industry: 'lead_gen',
  labelTh: 'Lead Generation',
  labelEn: 'Lead Generation',
  kpiType: 'leads',
  kpis: [
    { key: 'leads', label: 'จำนวน Lead', labelEn: 'Leads', higherIsBetter: true, unit: '' },
    { key: 'cpl', label: 'ต้นทุนต่อ Lead', labelEn: 'CPL', higherIsBetter: false, unit: '฿' },
    { key: 'quality_rate', label: 'อัตรา Lead คุณภาพ', labelEn: 'Quality Rate', higherIsBetter: true, unit: '%' },
    { key: 'roas', label: 'ROAS', labelEn: 'ROAS', higherIsBetter: true, unit: 'x' },
  ],
  optimizationWeights: {
    awareness: 0.7,
    consideration: 1.1,
    conversion: 1.3,
    retention: 0.3,
  },
  confidence: {
    baseConfidence: 82,
    deviationSensitivity: 1.0,
    floor: 50,
  },
  hasRetentionStage: false,
  proofPoints: [],
  cacRange: [500, 2000],
  avgConversionValue: 6000,
}

/**
 * E-commerce
 */
export const ECOMMERCE_INDUSTRY_CONFIG: IndustryConfig = {
  industry: 'ecommerce',
  labelTh: 'อีคอมเมิร์ซ',
  labelEn: 'E-commerce',
  kpiType: 'revenue',
  kpis: [
    { key: 'revenue', label: 'ยอดขาย', labelEn: 'Revenue', higherIsBetter: true, unit: '฿' },
    { key: 'roas', label: 'ROAS', labelEn: 'ROAS', higherIsBetter: true, unit: 'x' },
    { key: 'cpa', label: 'ต้นทุนต่อการซื้อ', labelEn: 'CPA', higherIsBetter: false, unit: '฿' },
    { key: 'cart_abandonment', label: 'อัตราทิ้งตะกร้า', labelEn: 'Cart Abandonment', higherIsBetter: false, unit: '%' },
  ],
  optimizationWeights: {
    awareness: 0.8,
    consideration: 1.0,
    conversion: 1.4,
    retention: 1.0,
  },
  confidence: {
    baseConfidence: 83,
    deviationSensitivity: 1.0,
    floor: 52,
  },
  hasRetentionStage: true,
  retentionMetric: 'repeat_purchases',
  proofPoints: [],
  cacRange: [150, 600],
  avgConversionValue: 1800,
}

/**
 * Automotive (traditional ICE)
 */
export const AUTOMOTIVE_INDUSTRY_CONFIG: IndustryConfig = {
  industry: 'automotive',
  labelTh: 'ยานยนต์',
  labelEn: 'Automotive',
  kpiType: 'leads',
  kpis: [
    { key: 'leads', label: 'จำนวน Lead', labelEn: 'Leads', higherIsBetter: true, unit: '' },
    { key: 'cpl', label: 'ต้นทุนต่อ Lead', labelEn: 'CPL', higherIsBetter: false, unit: '฿' },
    { key: 'test_drives', label: 'การทดลองขับ', labelEn: 'Test Drives', higherIsBetter: true, unit: '' },
    { key: 'bookings', label: 'การจอง', labelEn: 'Bookings', higherIsBetter: true, unit: '' },
  ],
  optimizationWeights: {
    awareness: 0.9,
    consideration: 1.2,
    conversion: 1.1,
    retention: 0.4,
  },
  confidence: {
    baseConfidence: 80,
    deviationSensitivity: 0.95,
    floor: 55,
  },
  hasRetentionStage: false,
  proofPoints: [],
  cacRange: [2500, 10000],
  avgConversionValue: 75000,
}

/**
 * General / default
 */
export const GENERAL_INDUSTRY_CONFIG: IndustryConfig = {
  industry: 'general',
  labelTh: 'ทั่วไป',
  labelEn: 'General',
  kpiType: 'revenue',
  kpis: [
    { key: 'revenue', label: 'ยอดขาย', labelEn: 'Revenue', higherIsBetter: true, unit: '฿' },
    { key: 'roas', label: 'ROAS', labelEn: 'ROAS', higherIsBetter: true, unit: 'x' },
    { key: 'cpl', label: 'ต้นทุนต่อ Lead', labelEn: 'CPL', higherIsBetter: false, unit: '฿' },
  ],
  optimizationWeights: {
    awareness: 1.0,
    consideration: 1.0,
    conversion: 1.0,
    retention: 0.5,
  },
  confidence: {
    baseConfidence: 80,
    deviationSensitivity: 1.0,
    floor: 50,
  },
  hasRetentionStage: false,
  proofPoints: [],
  cacRange: [200, 3000],
  avgConversionValue: 5000,
}

// ============================================================================
// INDUSTRY REGISTRY
// ============================================================================

/**
 * Central registry of all industry configurations.
 * Keyed by the industry string (matches FunnelConfig.industry).
 */
export const INDUSTRY_CONFIGS: Record<string, IndustryConfig> = {
  general: GENERAL_INDUSTRY_CONFIG,
  supplement: SUPPLEMENT_BRAND_CONFIG,
  ev: EV_BRAND_CONFIG,
  fmcg: FMCG_CONFIG,
  lead_gen: LEAD_GEN_INDUSTRY_CONFIG,
  ecommerce: ECOMMERCE_INDUSTRY_CONFIG,
  automotive: AUTOMOTIVE_INDUSTRY_CONFIG,
}

/**
 * Demo-critical industries in priority order.
 * These are the industries featured in the 30 Jun demo.
 */
export const DEMO_INDUSTRIES = [
  'supplement',
  'ev',
  'fmcg',
] as const

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get industry config by key. Falls back to general if not found.
 */
export function getIndustryConfig(industry: string): IndustryConfig {
  return INDUSTRY_CONFIGS[industry] ?? GENERAL_INDUSTRY_CONFIG
}

/**
 * Get the optimization weight for a funnel stage in an industry.
 * Maps the 3 standard FunnelStage keys to the weight fields.
 */
export function getStageWeight(
  industry: string,
  stageKey: string,
): number {
  const config = getIndustryConfig(industry)
  const weights = config.optimizationWeights

  switch (stageKey) {
    case 'aware':
      return weights.awareness
    case 'consider':
      return weights.consideration
    case 'convert':
      return weights.conversion
    case 'retain':
    case 'retention':
      return weights.retention
    default:
      return 1.0
  }
}

/**
 * Get the industry-specific base confidence for MMM calculations.
 */
export function getIndustryBaseConfidence(industry: string): number {
  return getIndustryConfig(industry).confidence.baseConfidence
}

/**
 * Get the industry-specific confidence deviation sensitivity.
 */
export function getDeviationSensitivity(industry: string): number {
  return getIndustryConfig(industry).confidence.deviationSensitivity
}

/**
 * Get the industry-specific confidence floor.
 */
export function getConfidenceFloor(industry: string): number {
  return getIndustryConfig(industry).confidence.floor
}

/**
 * Get all industry options for UI dropdowns.
 */
export function getIndustryOptions(): Array<{ value: string; labelTh: string; labelEn: string }> {
  return Object.values(INDUSTRY_CONFIGS).map((config) => ({
    value: config.industry,
    labelTh: config.labelTh,
    labelEn: config.labelEn,
  }))
}

/**
 * Get proof points for an industry (for demo/reports).
 */
export function getIndustryProofPoints(industry: string): IndustryProofPoint[] {
  return getIndustryConfig(industry).proofPoints
}

export default {
  INDUSTRY_CONFIGS,
  DEMO_INDUSTRIES,
  getIndustryConfig,
  getStageWeight,
  getIndustryBaseConfidence,
  getDeviationSensitivity,
  getConfidenceFloor,
  getIndustryOptions,
  getIndustryProofPoints,
}
